import { createSlice } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import { portalApi } from "../api/portalApi";

/**
 * Backward-compatible wrapper for postChangeLog.
 * Dispatches the RTK Query mutation and returns the result.
 */
export const postChangeLog = (payload) => (dispatch) => {
  return dispatch(portalApi.endpoints.postChangeLog.initiate(payload));
};

/**
 * Backward-compatible wrapper for getChangeLog.
 * Dispatches the RTK Query query for the given job ID.
 */
export const getChangeLog = (projectID) => (dispatch) => {
  if (!projectID) {
    console.warn('getChangeLog called without a valid projectID');
    return Promise.resolve({ data: [] });
  }

  return dispatch(
    portalApi.endpoints.getChangeLog.initiate(projectID, { forceRefetch: true })
  );
};

// Slice
const postChangeLogSlice = createSlice({
  name: "postChangeLog",
  initialState: {
    changeLogs: [],
    status: "idle",
    error: null,
  },
  reducers: {
    replaceRealtimeChangeLog: (state, action) => { state.changeLogs = action.payload; },
  },
  extraReducers: (builder) => {
    builder
      // ── RTK Query: postChangeLog ────────────────────────────────────────
      .addMatcher(
        portalApi.endpoints.postChangeLog.matchPending,
        (state) => { state.status = "loading"; }
      )
      .addMatcher(
        portalApi.endpoints.postChangeLog.matchFulfilled,
        (state) => {
          state.status = "succeeded";
          state.error = null;
        }
      )
      .addMatcher(
        portalApi.endpoints.postChangeLog.matchRejected,
        (state, action) => {
          state.status = "failed";
          state.error = action.error?.message ?? 'Failed to post change log';
        }
      )
      // ── RTK Query: getChangeLog ─────────────────────────────────────────
      .addMatcher(
        portalApi.endpoints.getChangeLog.matchPending,
        (state) => { state.status = "loading"; }
      )
      .addMatcher(
        portalApi.endpoints.getChangeLog.matchFulfilled,
        (state, action) => {
          state.status = "succeeded";
          state.changeLogs = action.payload;
          state.error = null;
        }
      )
      .addMatcher(
        portalApi.endpoints.getChangeLog.matchRejected,
        (state, action) => {
          state.status = "failed";
          state.error = action.error?.message ?? 'Failed to fetch change log';
        }
      );
  },
});

// Selectors
export const selectChangeLogs = createSelector(
  (state) => state.postChangeLog.changeLogs,
  (state) => state.jobId.jobId,
  (changeLogs, jobId) => changeLogs.filter((log) => String(log.ProjectID) === String(jobId))
);

export default postChangeLogSlice.reducer;
export const { replaceRealtimeChangeLog } = postChangeLogSlice.actions;
